import {
  Breadcrumbs,
  CardHeader,
  Divider,
  IconButton,
  Link as MuiLink,
  Typography,
  useTheme,
} from '@mui/material';
import { join, split } from 'lodash';
import { ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMuiSwitchableTheme } from '../../theme';
import { ArrowBackIos } from '@mui/icons-material';
import { Container } from '@mui/system';

interface PageTitleProps {
  title: ReactNode;
  subTitle?: ReactNode;
  breadCrumbLabel?: string;
  action?: ReactNode;
  backAction?: () => void;
  innerBreadcrumbs?: { text: string; to: string }[];
  ellipsis?: ReactNode;
  showHr?: boolean;
  // Set to false if you only want document.title to change for page tracking
  show?: boolean;

  // Set to false if you don't want to render breadcrumbs
  showBreadCrumbs?: boolean;
  sticky?: boolean;
  showBackButton?: boolean;
  responsiveActionButton?: boolean;
}

export function PageTitle({
  title,
  subTitle,
  innerBreadcrumbs,
  breadCrumbLabel,
  ellipsis,
  action = null,
  showHr = true,
  show = true,
  showBreadCrumbs = true,
  sticky = false,
  showBackButton = false,
  backAction = () => window.history.back(),
  responsiveActionButton = false,
}: PageTitleProps) {
  useEffect(() => {
    if (title && typeof title === 'string') {
      const delimiter = ' | ';
      const [base] = split(window.document.title, delimiter);
      window.document.title = join([base, breadCrumbLabel || title], delimiter);
    }
  }, [breadCrumbLabel, title]);

  const theme = useTheme();

  const { marginTop, marginLeft } = useMuiSwitchableTheme();

  if (!show) {
    return null;
  }

  return (
    <Container
      sx={{
        minWidth: 1,
        maxWidth: `calc(100vw - ${marginLeft}px)`,
        ...(sticky
          ? {
              zIndex: theme.zIndex.appBar - 1,
              px: 4,
              mx: -3,
              backgroundColor: 'background.default',
              position: 'sticky',
              top: marginTop,
              left: marginLeft,
              right: 0,
            }
          : {
              px: 1,
            }),
      }}
    >
      {showBreadCrumbs && (
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{ mb: 1, fontSize: 14 }}
          separator="›"
        >
          <MuiLink
            underline="hover"
            color="inherit"
            component={Link}
            to="/"
            fontWeight="normal"
          >
            Home
          </MuiLink>
          {innerBreadcrumbs?.map(({ text, to }, idx) => (
            <MuiLink
              fontWeight="normal"
              key={idx}
              underline="hover"
              color="inherit"
              component={Link}
              to={to}
            >
              {text}
            </MuiLink>
          ))}
          <Typography
            sx={{ color: 'text.primary', fontWeight: 'medium', fontSize: 14 }}
            color="text.primary"
          >
            {breadCrumbLabel || title}
          </Typography>
        </Breadcrumbs>
      )}
      <CardHeader
        avatar={
          showBackButton && (
            <IconButton onClick={backAction} size="small" sx={{ ml: 1 }}>
              <ArrowBackIos fontSize="small" />
            </IconButton>
          )
        }
        title={title}
        subheader={subTitle}
        action={action}
        sx={{
          p: 0,
          ...(!responsiveActionButton
            ? {
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                '& .MuiCardHeader-content': {
                  alignSelf: { xs: 'flex-start', sm: 'center' },
                },
              }
            : {}),
          mb: 1,
        }}
        slotProps={{
          title: {
            fontSize: '1.4rem',
            color: 'text.primary',
            textTransform: 'capitalize',
          },
          subheader: {
            fontSize: '1rem',
            color: 'text.secondary',
          },
          action: {
            sx: {
              justifyContent: 'flex-end',
              display: 'flex',
              alignSelf: 'flex-start',
              mb: { xs: 1, sm: 0 },
              mt: { xs: 0, sm: 0 },
              ...(!responsiveActionButton
                ? {
                    width: { xs: 1, sm: 'auto' },
                  }
                : { width: 'auto' }),
            },
          },
        }}
      />
      {ellipsis}

      {showHr && <Divider sx={{ my: 1, opacity: 1 }} />}
    </Container>
  );
}
